<style scoped lang="scss">
form {
    h2 {
        color: #000;
    }
    ::v-deep(.formkit-select-icon) {
        height: 50px;
        margin: 0;
        padding: 0;
    }
}
main .container .card {
    background-color: rgba(91, 91, 98, 0.08);
}
</style>

<template>
    <div class="mb-5">
        <h1 class="page-title">{{ title }}</h1>
    </div>
    <div class="max-w-screen-xl mx-auto">
        <div class="flex flex-wrap -mx-4">
            <div class="w-full lg:w-1/4 px-3">
                <div class="mb-5">
                    <div class="card w-75 shadow-xl">
                        <div class="card-body items-center text-center">
                            <h5 class="card-title">Image</h5>
                            <UploadImage :image-preview-url="form.image ? form.image.url : getBaseUrl() + '/media/blank2.png'" @image-removed="onImageRemoved" @imageUploaded="onImageUpload" @error="onImageUploadError" />
                        </div>
                    </div>
                </div>
                <div class="mb-5">
                    <div class="card w-75 shadow-xl">
                        <div class="card-body items-center text-center">
                            <h5 class="card-title">Icon 3D</h5>
                            <UploadImage :image-preview-url="form.icon3d ? form.icon3d.url : getBaseUrl() + '/media/blank2.png'" @image-removed="onImageIconRemoved" @imageUploaded="onImageIconUpload" @error="onImageUploadError" />
                        </div>
                    </div>
                </div>
            </div>
        <div class="w-full lg:w-3/4 px-4">
        <div class="card w-full shadow-xl">
        <div class="card-body">
            <div class="w-full">
                <FormKit type="form"
                 :actions="false"
                 :model="form"
                 @submit="onSubmit"
                 #default="{ state: { valid } }"
                 outer-class="w-full"
                 incomplete-message="Veuillez remplir tous les champs correctement"
                >
                <FormKit type="text"
                 name="title"
                 :value="middleMenu.title"
                 v-model="middleMenu.title"
                 label="Titre *" 
                 validation="required|contains_alpha|matches:/^[^/]*$/"
                 :validation-messages="{
                    required: 'Ce champ est obligatoire.',
                    contains_alpha: 'Ce champ doit contenir des lettres..',
                    matches: 'Ce champ ne peut contenir de “/”.'
                 }"
                />
                <FormKit type="number"
                 name="position"
                 label="Position *"
                 :value="middleMenu.position"
                 v-model="middleMenu.position"
                 validation="required|min:1"
                 :validation-messages="{
                    required: 'Ce champ est obligatoire.',
                    min: 'Ce champ doit être un nombre décimal superieur à 0',
                }"
                />
                    <FormKit
                        type="text"
                        name="reference"
                        label="Identifiant *"
                        validation="required"
                        :value="middleMenu.reference"
                        v-model="middleMenu.reference"
                        :validation-messages="{
                            required: 'Ce champ est obligatoire.'
                        }"
                        :disabled="referenceInitiallySet"
                    />
                <FormKit
                    v-if="type == 'region' && shouldShowAdditionalInfo"
                    type="text"
                    name="additionalInfo"
                    label="Références numériques *"
                    :value="middleMenu.additionalInfo"
                    v-model="middleMenu.additionalInfo"
                    validation="required"
                    :validation-messages="{
                        required: 'Ce champ est obligatoire.',
                       }"
                />
                <p class="text-sm text-gray-500 mt-1 mb-2" v-if="type == 'region' && shouldShowAdditionalInfo">
                    Format attendu : nombres à 2 chiffres, séparés par des virgules. Exemple : <strong>12, 34, 56</strong>
                </p>
                <div class="card-actions">
                    <div v-if="loading === false">
                        <FormKit type="submit"
                         :label="submitBtnLabel"
                         :disabled="!valid"
                         :class="{ 'btn-primary': true, 'btn-block': true }"
                        />
                    </div>
                    <FormKit type="button"
                     label="Annuler"
                     @click="onCancel"
                     :class="{ 'btn-secondary': true, 'btn-block': true }"
                    />
                    </div>
                </FormKit>
            </div>
        </div>
        </div>
        </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { onBeforeMount, ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router'
import ApiService from "@/_services/api.service";
import swal from "@/_helpers/swal";
import {fetchByUuid} from "@/_services/homepage/middleMenu";
import {IMiddleMenu} from "@/models/homepage/IMiddleMenu";
import UploadImage from "@/components/forms/UploadImage.vue";
const title = ref<string>("Edition d'une référence");
const submitBtnLabel = 'éditer';
const middleMenu = ref<IMiddleMenu>({} as IMiddleMenu);
const route = useRoute();
const uuidRouteParam = ref<string>("");
const router = useRouter();
const loading = ref<boolean>(true);
const referenceInitiallySet = ref(false);
const configMoney = ref<{ locale?: string }>({})

const allowedTypes = ['region', 'travel']

const type = computed(() => {
    const current = route.params.type as string | undefined
    return allowedTypes.includes(current ?? '') ? current : null
})

if (route.params.type && !allowedTypes.includes(route.params.type as string)) {
    router.replace({ name: 'editMiddleMenu' })
}

onMounted(async () => {
    try {
        const { data } = await ApiService.get('/config-money')
        configMoney.value = data.data
    } catch (e) {
        console.error('Erreur récupération config-money', e)
    }
})

const shouldShowAdditionalInfo = computed(() => {
    const value = (configMoney.value?.locale ?? 'fr_FR').toLowerCase()
    return !value.includes('en') ?? true
})

onBeforeMount(async () => {
    loading.value = true;
    uuidRouteParam.value = route.params.uuid.length ? route.params.uuid as string : "";
    if (uuidRouteParam.value.length) {
        middleMenu.value = await fetchByUuid(uuidRouteParam.value);
        await hydrateForm(middleMenu.value);
        if (middleMenu.value.reference) {
            referenceInitiallySet.value = true;
        }
        loading.value = false;
    }
});

const form = ref<IMiddleMenu>({
    image: null,
    icon3d: null
});

const hydrateForm = (middleMenu: IMiddleMenu) => {
    form.value = {
        image: middleMenu.image,
        icon3d: middleMenu.icon3d
    }
}

const onSubmit = async (formData) => {
    try {
        const payload = {
            position: parseInt(formData.position),
            title: formData.title,
            additionalInfo: formData.additionalInfo,
            image: form.value.image?.uuid ?`/api/images/${form.value.image.uuid}` : null,
            icon3d: form.value.icon3d?.uuid ? `/api/images/${form.value.icon3d.uuid}` : null,
            reference: formData.reference ?? null,
        }
        swal.confirm(`Êtes-vous sûr de vouloir ${submitBtnLabel} ?`,
            "Annuler",
            submitBtnLabel
        )
            .then((results) => results.isConfirmed && update(payload))
    } catch (err: any) {
        swal.error(err.errors[0]);
    }
}

const update = (data) => {
    if(uuidRouteParam.value.length) {
        ApiService.patch('middle_menus', uuidRouteParam.value, data)
            .then(() => {
                loading.value = false;
                swal.info("Edition avec succès")
                    .then((result) => {
                        if (result.isConfirmed) {
                            if (type.value && type.value == "region") {
                                router.push({
                                    name: "regionList"
                                });
                            } else if (type.value && type.value == "travel") {
                                router.push({
                                    name: "travelList"
                                });
                            } else {
                                router.push({
                                    name: "middleMenuList"
                                });
                            }
                        }
                    });
            })
            .catch(() => {
                swal.error("Une erreur est survenue lors de la modification");
            });
    }
}

const onCancel = () => {
    swal.confirm(
        "Êtes-vous sûr de vouloir annuler l'édition ?",
        "Oui",
        "Non"
    ).then((result) => {
        if (result.isDismissed) {
            if (type.value && type.value == "region") {
                router.push({
                    name: "regionList"
                });
            } else if (type.value && type.value == "travel") {
                router.push({
                    name: "travelList"
                });
            } else {
                router.push({
                    name: "middleMenuList"
                });
            }
        }
    });
}

const onImageUpload = (image) => {
    form.value.image = image;
}

const onImageIconUpload = (image) => {
    form.value.icon3d = image;
}

const onImageRemoved = () => {
    form.value.image = null;
}

const onImageIconRemoved = () => {
    form.value.icon3d = null;
}

const onImageUploadError = (errorMsg: string) => {
    swal.error(errorMsg);
}

function getBaseUrl() {
    return window.location.origin;
}
</script>